import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsService, HeadcountRow, HeadcountReportFilters } from '../../services/api/reports';
import { departmentsService } from '../../services/api/departments';
import { Department } from '../../types';
import './HeadcountReport.css';

const HeadcountReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<HeadcountRow[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filters, setFilters] = useState<HeadcountReportFilters>({
    month: new Date().toISOString().slice(0, 7), // Current month YYYY-MM
    includeSubtree: false,
  });

  useEffect(() => {
    loadDepartments();
    loadReport();
  }, []);

  useEffect(() => {
    loadReport();
  }, [filters]);

  const loadDepartments = async () => {
    try {
      const depts = await departmentsService.getAll();
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await reportsService.getHeadcountReport(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to load headcount report:', error);
      alert('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof HeadcountReportFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      await reportsService.exportHeadcountReport(filters);
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalActive = data.reduce((sum, row) => sum + row.activeHeadcount, 0);
  const totalInactive = data.reduce((sum, row) => sum + row.inactiveHeadcount, 0);
  const totalJoiners = data.reduce((sum, row) => sum + row.joinersInMonth, 0);

  return (
    <div className="headcount-report">
      <div className="report-header">
        <button className="back-button" onClick={() => navigate('/reports')}>
          ← Back to Reports
        </button>
        <div>
          <h1>Headcount by Department (RPT-EMP-01)</h1>
          <p>View active/inactive headcount and new joiners by department</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="filter-group">
          <label>Month</label>
          <input
            type="month"
            value={filters.month || ''}
            onChange={(e) => handleFilterChange('month', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Department</label>
          <select
            value={filters.departmentId || ''}
            onChange={(e) =>
              handleFilterChange('departmentId', e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">All Departments</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={filters.includeSubtree || false}
              onChange={(e) => handleFilterChange('includeSubtree', e.target.checked)}
            />
            Include Sub-departments
          </label>
        </div>
        <button className="export-button" onClick={handleExport} disabled={loading}>
          {loading ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading report...</div>
      ) : (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">Total Active</div>
              <div className="summary-value">{totalActive}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Inactive</div>
              <div className="summary-value">{totalInactive}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Joiners (Month)</div>
              <div className="summary-value">{totalJoiners}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Headcount</div>
              <div className="summary-value">{totalActive + totalInactive}</div>
            </div>
          </div>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Department Code</th>
                  <th>Department Name</th>
                  <th>Active</th>
                  <th>Inactive</th>
                  <th>Joiners (Month)</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="no-data">
                      No data available
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr key={row.departmentId}>
                      <td>{row.departmentCode}</td>
                      <td>{row.departmentName}</td>
                      <td className="number-cell">{row.activeHeadcount}</td>
                      <td className="number-cell">{row.inactiveHeadcount}</td>
                      <td className="number-cell">{row.joinersInMonth}</td>
                      <td className="number-cell">{row.totalHeadcount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default HeadcountReport;
